/** @jsxImportSource theme-ui */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { InsightsState } from 'app-redux/InsightsState';
import * as dto from 'client/dto';
import { countRiskFactors } from 'models/DisruptionModels';
import { calculateToWholePercent } from 'utils/mathHelpers';
import {
  getRiskLikelihood,
  getRiskMagnitude,
  simpleTimeFrameDescription,
} from 'utils/modelHelpers';
import { formatCaseCountPrevalence } from 'utils/stringFormattingHelpers';

import { BdCaseIncidenceIcon } from 'components/_common/Icon/BdCaseIncidenceIcon';
import BdCaseIncidenceInheritedIcon from 'components/_common/Icon/BdCaseIncidenceIcon/BdCaseIncidenceInheritedIcon';
import { BdDisruptionRiskIcon } from 'components/_common/Icon/BdDisruptionRiskIcon';
import { BdImportationRiskIcon } from 'components/_common/Icon/BdImportationRiskIcon';
import { BdAccordion, BdAccordionSection } from 'components/_controls/BdAccordion';

import locationsSelectors from 'locations/locations.selectors';
import { fetchDiseaseRiskImportationSources } from 'riskOverview/riskOverview.saga';
import riskOverviewSelectors from 'riskOverview/riskOverview.selectors';

import RiskOverviewCardActivityDetails from '../RiskOverviewCardActivityDetails';
import RiskOverviewCardCaseImportationDetails from '../RiskOverviewCardCaseImportationDetails';
import RiskOverviewCardDisruptionDetails from '../RiskOverviewCardDisruptionDetails';
import RiskOverviewCardSectionTitle from '../RiskOverviewCardSectionTitle';

const TimeFrame = '60days';

type RiskOverviewCardAccordionProps = { diseaseActivityId: number; locationId: number };

const RiskOverviewCardAccordion: React.FC<RiskOverviewCardAccordionProps> = ({
  diseaseActivityId,
  locationId,
}) => {
  const dispatch = useDispatch();

  const locationName = useSelector(locationsSelectors.getLocationName(locationId));

  const riskFeedEntry = useSelector((state: InsightsState) =>
    riskOverviewSelectors.getRiskFeedEntryByActivityIdAndLocationId(state)
  )(diseaseActivityId, locationId);

  const riskMagnitude =
    riskFeedEntry != null ? getRiskMagnitude(riskFeedEntry.importationRisk) : null;
  const riskLikelihood =
    riskFeedEntry != null ? getRiskLikelihood(riskFeedEntry.importationRisk) : 'Not calculated';

  const disruption: dto.DisruptionModel =
    riskFeedEntry?.disruption != null
      ? {
          ...riskFeedEntry?.disruption,
          locationResiliency: calculateToWholePercent(
            riskFeedEntry?.disruption?.locationResiliency
          ),
        }
      : null;

  return (
    <BdAccordion>
      <BdAccordionSection
        title={
          <RiskOverviewCardSectionTitle
            heading={`Activity in ${locationName}`}
            title={
              <>
                {riskFeedEntry?.caseIncidence?.casesPerPopulation
                  ? formatCaseCountPrevalence(riskFeedEntry?.caseIncidence?.casesPerPopulation)
                  : '—'}
                {riskFeedEntry?.caseIncidence?.isInherited && <BdCaseIncidenceInheritedIcon />}
              </>
            }
            subtitle={`Cases per 100,000 (${simpleTimeFrameDescription(
              TimeFrame,
              null
            ).toLocaleLowerCase()})`}
            icon={
              <BdCaseIncidenceIcon
                caseCountPrevalence={riskFeedEntry?.caseIncidence?.casesPerPopulation}
                isCaseDataAvailable={riskFeedEntry?.hasReportedData}
                simple
              />
            }
          />
        }
      >
        <RiskOverviewCardActivityDetails
          locationId={locationId}
          diseaseActivityId={diseaseActivityId}
          timeFrame={TimeFrame}
        />
      </BdAccordionSection>
      <BdAccordionSection
        onOpen={() => {
          dispatch(
            fetchDiseaseRiskImportationSources({
              diseaseActivityId: diseaseActivityId,
              locationId: locationId,
            })
          );
        }}
        title={
          <RiskOverviewCardSectionTitle
            heading="Risk of Case Importation"
            title={riskMagnitude ?? '—'}
            subtitle="Estimated case importations per month"
            icon={
              <BdImportationRiskIcon
                riskMagnitude={riskMagnitude}
                riskLikelihood={riskLikelihood}
              />
            }
          />
        }
        sx={{
          padding: '0',
        }}
      >
        <RiskOverviewCardCaseImportationDetails
          title={riskMagnitude ?? '—'}
          subtitle="Estimated case importations per month"
          locationId={locationId}
          diseaseId={riskFeedEntry?.diseaseId}
          diseaseActivityId={diseaseActivityId}
          diseaseRiskMagnitude={riskMagnitude}
          riskLikelihood={riskLikelihood}
        />
      </BdAccordionSection>
      <BdAccordionSection
        title={
          <RiskOverviewCardSectionTitle
            heading="Risk of Disruption"
            title={`${countRiskFactors(disruption)}/4 risk factors`}
            subtitle="Disruptive potential"
            icon={<BdDisruptionRiskIcon disruption={disruption} simple />}
          />
        }
      >
        <RiskOverviewCardDisruptionDetails
          locationId={locationId}
          diseaseActivityId={diseaseActivityId}
        />
      </BdAccordionSection>
    </BdAccordion>
  );
};

export default RiskOverviewCardAccordion;
